import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { FileText, Download, FolderOpen } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { DOC_CATEGORIES, formatSize, openSharedDoc } from "@/lib/sharedDocs";

function DocRow({ d, onOpen }) {
  return (
    <button
      onClick={() => onOpen(d)}
      data-testid={`shared-doc-${d.id}`}
      className="w-full p-4 flex items-center gap-3 hover:bg-stone-50/60 transition-colors duration-150 text-left"
    >
      <div className="w-10 h-10 rounded-lg bg-stone-50 border border-stone-200 grid place-items-center shrink-0">
        <FileText size={18} className={d.extension === "pdf" ? "text-red-600" : "text-stone-500"} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[#1C352D] truncate flex items-center gap-2">
          {d.title}
          {!d.seen && (
            <span data-testid={`shared-doc-new-${d.id}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E2725B] text-white font-semibold">Nuovo</span>
          )}
        </div>
        {d.description && <div className="text-xs text-stone-600 mt-0.5 line-clamp-2">{d.description}</div>}
        <div className="text-[11px] text-stone-500 truncate mt-0.5">
          {d.original_filename} · {formatSize(d.size)} · pubblicato {fmtDate(d.created_at)}
        </div>
      </div>
      <Download size={16} className="text-stone-400 shrink-0" />
    </button>
  );
}

export function SharedDocsList() {
  const qc = useQueryClient();
  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["shared-docs-me"],
    queryFn: async () => (await api.get("/shared-documents/me")).data,
  });

  function open(d) {
    openSharedDoc(d.id);
    setTimeout(() => {
      qc.invalidateQueries({ queryKey: ["shared-docs-me"] });
      qc.invalidateQueries({ queryKey: ["shared-docs-unseen"] });
    }, 1500);
  }

  if (isLoading) return <div className="text-sm text-stone-400 text-center py-8">Caricamento…</div>;
  if (docs.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none" data-testid="shared-docs-empty">
        <FolderOpen size={26} className="mx-auto mb-3 text-stone-300" />
        Nessun documento pubblicato dalla segreteria.
      </Card>
    );
  }
  return (
    <div className="space-y-6" data-testid="shared-docs-list">
      {DOC_CATEGORIES.map((c) => {
        const items = docs.filter((d) => d.category === c.value);
        if (!items.length) return null;
        return (
          <section key={c.value} data-testid={`shared-docs-cat-${c.value}`}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 mb-2">{c.label}</div>
            <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
              <div className="divide-y divide-stone-100">
                {items.map((d) => <DocRow key={d.id} d={d} onOpen={open} />)}
              </div>
            </Card>
          </section>
        );
      })}
    </div>
  );
}
